import {
  type FC,
  type MouseEvent,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CaretDownIcon,
  CaretUpIcon,
  NetworkIcon,
  PlusCircleIcon,
  SignOutIcon,
} from "@phosphor-icons/react";
import { useHistory } from "react-router-dom";
import TetherComponent from "react-tether";

import { classNames, Logo, SidebarState, UIContext } from "@alpacahq/ui/old";

import { UserActions } from "~/v2/api/auth";
import { useGetMargins } from "~/v2/api/hooks/useGetMargin";
import type { Account, Margin, PaperAccount } from "~/v2/api/rest/account";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { getPercentageChange, isPaperOrOnboarding } from "~/v2/utils";
import {
  getLiveAccountLabels,
  getPaperAccountLabels,
} from "~/v2/utils/accountLabels";
import { Path } from "../../path";
import CreatePaperAccountModal from "./CreatePaperAccountModal";
import SidebarAccountItem, {
  SidebarAccountButton,
  type SidebarAccountItemProperties,
} from "./SidebarAccountItem";

export const MAX_PAPER_ACCOUNTS = 3;

export interface SidebarAccountSwitcherProps {
  darkLogo?: boolean;
  ownerId?: string;
  dashboardTradeAccountMap?: Record<string, Margin>;
}

const getSidebarAccountItemProperties = ({
  product,
  accounts,
  margins,
}:
  | {
      product: "live";
      accounts?: Account[];
      margins?: Margin[];
    }
  | {
      product: "paper";
      accounts?: PaperAccount[];
      margins?: Margin[];
    }): SidebarAccountItemProperties[] => {
  if (product === "paper") {
    return (
      accounts?.map((account) => {
        const margin = margins?.find((m) => m.id === account.paper_account_id);

        return {
          id: account.paper_account_id,
          ...getPaperAccountLabels({ account, margin }),
          percentage:
            margin && getPercentageChange(margin.last_equity, margin.equity),
          equity: margin?.equity,
          last_equity: margin?.last_equity,
        };
      }) ?? []
    );
  }

  if (product === "live") {
    return (
      accounts?.map((account) => {
        const margin = margins?.find((m) => m.id === account.id);

        return {
          id: account.id,
          ...getLiveAccountLabels({ account, margin }),
          hideAccountMetrics: isPaperOrOnboarding(account),
          percentage:
            margin && getPercentageChange(margin.last_equity, margin.equity),
          equity: margin?.equity,
          last_equity: margin?.last_equity,
        };
      }) ?? []
    );
  }

  return [];
};

const SidebarAccountSwitcher: FC<SidebarAccountSwitcherProps> = ({
  darkLogo,
}) => {
  const { sidebarState, setIsSidebarBlurred } = useContext(UIContext);
  const history = useHistory();

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpacity, setDropdownOpacity] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(false);

  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);

  const {
    isLoading,
    currentAccount,
    setCurrentAccount,
    liveAccounts,
    paperAccounts,
    paperAccountsNoDetail,
  } = useAccountContext();

  const { margin: liveMargins } = useGetMargins({
    accountIDs: liveAccounts.map((a) => a.id),
    paper: false,
    options: {
      enabled: !!liveAccounts.length,
    },
  });

  const { margin: paperMargins } = useGetMargins({
    accountIDs: paperAccountsNoDetail.map(
      ({ paper_account_id }) => paper_account_id,
    ),
    paper: true,
    options: {
      enabled: !!paperAccountsNoDetail.length,
    },
  });

  const handleSwitchAccount = (newAccount: SidebarAccountItemProperties) => {
    // If attempting to switch to a live account while paper or onboarding, redirect to onboarding.
    const liveAccount = liveAccounts?.find((a) => a.id === newAccount.id);
    if (liveAccount && isPaperOrOnboarding(liveAccount)) {
      history.push(Path.ROUTE_NEW_ACCOUNT);
    } else {
      setCurrentAccount(newAccount.id);
    }
  };

  const liveAccountItems = getSidebarAccountItemProperties({
    product: "live",
    accounts: liveAccounts,
    margins: liveMargins,
  });

  const paperAccountItems = getSidebarAccountItemProperties({
    product: "paper",
    accounts: paperAccountsNoDetail,
    margins: paperMargins,
  });

  const currentSidebarAccount = useMemo(() => {
    const account =
      liveAccountItems.find((a) => a.id === currentAccount?.id) ||
      paperAccountItems.find((pa) => pa.id === currentAccount?.id);
    return (
      account ??
      ({
        title: "Loading...",
        subTitle: "",
      } as SidebarAccountItemProperties)
    );
  }, [liveAccountItems, paperAccountItems, currentAccount]);

  // short-form for checking if the sidebar is collapsed
  const isCollapsed = sidebarState === SidebarState.COLLAPSED;

  useEffect(() => {
    // blur the sidebar when the dropdown is open to focus on the dropdown
    setIsSidebarBlurred(isOpen);
  }, [isOpen, setIsSidebarBlurred]);

  useEffect(() => {
    // close the dropdown when clicking outside of it
    // granted it may be a click on the dropdown itself, but
    // we want to close it anyway when you click an item
    const handleClickOutside = () => setIsOpen(false);
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // due to positioning issues with the dropdown, we need to force a re-render after a delay
  // the dropdown will not get cut-off from parent scroll containers this way
  const toggleOpen = (event: MouseEvent) => {
    event.stopPropagation();

    setIsOpen(!isOpen);
    setDropdownOpacity(0);

    if (!isOpen) {
      setTimeout(() => {
        setForceUpdate(true);
        setDropdownOpacity(1);
      }, 100);
    }
  };

  const handleUpdate = useCallback(() => {
    // force a re-render after the first update
    if (forceUpdate) {
      setForceUpdate(false);
    }
  }, [forceUpdate]);

  return (
    <div className="h-14">
      <TetherComponent
        attachment="top left"
        targetAttachment="bottom left"
        offset="-12px 0"
        onUpdate={handleUpdate}
        // @ts-expect-error @types/react-tether describes v0.5's children API and
        // declares no renderTarget, so passing v3's render prop is TS2769 and its
        // ref an implicit any (TS7006). v3 ships correct types in
        // lib/react-tether.d.ts that this tsconfig's resolution does not pick up.
        renderTarget={(ref) => (
          <button
            type="button"
            disabled={isLoading}
            ref={ref}
            onClick={toggleOpen}
            data-testid="account-switcher-button"
            className={classNames(
              // base styles
              "flex h-14 w-full items-center gap-1 overflow-hidden rounded border border-cool-gray-200 p-2",
              // collapsed styles
              !isCollapsed
                ? "justify-between"
                : "justify-center bg-transparent",
              "cursor-pointer disabled:cursor-progress",
              isOpen && "bg-cool-gray-50",
            )}
          >
            <div className="flex w-full items-center gap-2 overflow-hidden">
              <Logo
                className={classNames("h-6 w-6", darkLogo && "text-gray-900")}
              />
              {!isCollapsed && (
                <div className="flex w-full flex-col overflow-hidden text-left">
                  <div className="truncate whitespace-nowrap font-semibold text-black text-sm">
                    {currentSidebarAccount?.title}
                  </div>
                  <div className="truncate whitespace-nowrap font-medium text-gray-500 text-xs">
                    {currentSidebarAccount?.collapsedSubTitle}
                  </div>
                </div>
              )}
            </div>
            {!isCollapsed &&
              (isOpen ? (
                <CaretUpIcon size={20} />
              ) : (
                <CaretDownIcon size={20} />
              ))}
          </button>
        )}
        renderElement={(ref: RefObject<HTMLDialogElement>) =>
          isOpen && (
            <dialog
              ref={ref}
              style={{ opacity: dropdownOpacity }}
              className="flex max-h-[calc(100vh-8rem)] w-[348px] flex-col gap-4 overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 shadow-[0_4px_8px_0_rgba(0,0,0,0.1)] sm:w-[380px]"
              data-testid="account-switcher-dropdown"
              aria-label="Account Switcher"
            >
              <section className="flex flex-col gap-3">
                <h3 className="m-0 p-0 font-bold text-gray-700 text-sm">
                  LIVE ACCOUNT
                </h3>
                {liveAccountItems.map((account) => (
                  <SidebarAccountItem
                    key={account.id}
                    {...account}
                    active={account.id === currentAccount?.id}
                    onClick={() => handleSwitchAccount(account)}
                    data-testid={`account-item-live-${account.id}`}
                  />
                ))}
              </section>
              <section className="flex flex-col gap-3">
                <h3 className="m-0 p-0 font-bold text-gray-700 text-sm">
                  PAPER ACCOUNT
                </h3>
                <div className="flex flex-col gap-3">
                  {paperAccountItems.length > 0 ? (
                    paperAccountItems.map((account) => (
                      <SidebarAccountItem
                        key={account.id}
                        {...account}
                        active={account.id === currentAccount?.id}
                        onClick={() => handleSwitchAccount(account)}
                        data-testid={`account-item-paper-${account.id}`}
                      />
                    ))
                  ) : (
                    <div className="py-3 text-start text-gray-600 text-sm">
                      No Paper Accounts
                    </div>
                  )}
                </div>
              </section>

              <section className="flex flex-col gap-3">
                {paperAccounts.length < MAX_PAPER_ACCOUNTS && (
                  <SidebarAccountButton
                    icon={<PlusCircleIcon size={20} />}
                    label="New Paper Account"
                    onClick={() => setIsCreationModalOpen(true)}
                  />
                )}
                <SidebarAccountButton
                  icon={<NetworkIcon size={20} />}
                  label="Account Management"
                  onClick={() =>
                    history.push(Path.ROUTE_PROFILE_MANAGE_ACCOUNTS)
                  }
                />
                <div className="my-1 h-px w-full bg-cool-gray-200" />
                <button
                  className="flex items-center justify-center gap-1.5 rounded border border-transparent bg-white p-1.5 px-4 text-center font-medium text-rose-500 text-sm transition-all hover:border-rose-500 hover:bg-rose-50"
                  onClick={() => UserActions.userLogout({ global: true })}
                  type="button"
                >
                  <SignOutIcon size={20} />
                  Sign Out
                </button>
              </section>
            </dialog>
          )
        }
      />
      {isCreationModalOpen && (
        <CreatePaperAccountModal
          isOpen
          onClose={() => setIsCreationModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SidebarAccountSwitcher;
